import React, { useEffect, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Select,
    MenuItem,
    TablePagination,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { de } from 'date-fns/locale/de';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import config from "./config";

function DiscountsTable({ placeId }) {

    const [discounts, setDiscounts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentDiscount, setCurrentDiscount] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [doubleConfirmOpen, setDoubleConfirmOpen] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [newDiscount, setNewDiscount] = useState({
        discountVal: '',
        discountType: '',
        code: '',
        branchNum: '',
    });

    const [isDeviationValid, setIsDeviationValid] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(8);


    const [isLastNumeratorDialogOpen, setIsLastNumeratorDialogOpen] = useState(false);
    // const [discountForLastNumEdit, setDiscountForLastNumEdit] = useState(null);
    const [newLastNumerator, setNewLastNumerator] = useState('');


    useEffect(() => {
        fetch(`${config.app.serverUrl}/getdiscountstable?token=${localStorage.getItem('googleAuthToken')}&placeId=${placeId}`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Error loading discounts");
                }
                return response.json();
            })
            .then((data) => {
                setDiscounts(data || []);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching discounts:", error);
                setError("Error loading discounts. Please try again.");
                setIsLoading(false);
            });
    }, [placeId]);


    const handleDeleteClick = (discount) => {
        setDeleteConfirm(discount);
        setDoubleConfirmOpen(true);
    };

    const confirmDelete = () => {
        if (deleteConfirm) {
            handleDelete(deleteConfirm.id);
            setDeleteConfirm(null);
            setDoubleConfirmOpen(false);
        }
    };

    const cancelDelete = () => {
        setDeleteConfirm(null);
        setDoubleConfirmOpen(false);
    };

    const handleDelete = (id) => {
        fetch(`${config.app.serverUrl}/deletediscount/${id}?token=${localStorage.getItem('googleAuthToken')}`, {
            method: 'GET',
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Error deleting discount");
                }
                setDiscounts((prev) => {
                    const updatedDiscounts = prev.filter((discount) => discount.id !== id);
                    if (updatedDiscounts.length <= page * rowsPerPage && page > 0) {
                        setPage((prevPage) => prevPage - 1);
                    }
                    return updatedDiscounts;
                });
            })
            .catch((error) => {
                console.error("Error deleting discount:", error);
            });
    };


    const handleEdit = (discount) => {
        setCurrentDiscount(discount);
        setOpenDialog(true);
    };

    const handleCreate = () => {
        setCurrentDiscount(null);
        setNewDiscount({
            discountVal: '',
            discountType: 'time',
            code: '',
            branchNum: discounts.length > 0 ? discounts[0].branchNum : '',
            globalNumerator: discounts.length > 0 ? discounts[0].globalNumerator : '',
            deviation: discounts.length > 0 ? discounts[0].deviation : null,
            timeToUpdate: discounts.length > 0 ? discounts[0].timeToUpdate : ''
        });
        setOpenDialog(true);
    };

    const handleInputChange = (event, isNew = false) => {
        const { name, value } = event.target;

        // Only numbers for these fields
        if (['branchNum', 'code', 'discountVal', 'deviation'].includes(name)) {
            if (!/^[0-9]*$/.test(value)) return;
        }

        if (name === 'deviation') {
            const isValid = validateDeviation(value);
            setIsDeviationValid(isValid);
        }

        // Limit branchNum to 3 digits
        if (name === 'branchNum' && value.length > 3) return;

        // Limit code to 5 digits
        if (name === 'code' && value.length > 5) return;

        if (isNew) {
            setNewDiscount((prev) => ({ ...prev, [name]: value }));
        } else {
            setCurrentDiscount((prev) => ({ ...prev, [name]: value }));
        }
    };

    const validateDeviation = (value) => {
        const MIN = -9223372036854775808;
        const MAX = 9223372036854775807;
        if (value === "") return true; // Empty is valid if you allow optional
        const numericValue = Number(value);
        return numericValue >= MIN && numericValue <= MAX;
    };

    // Validate main discount fields
    const validateFields = (discount) => {
        if (!discount.discountVal) {
            alert("Discount value is required.");
            return false;
        }
        if (!discount.discountType) {
            alert("Discount type is required.");
            return false;
        }
        if (!discount.code) {
            alert("Discount code is required.");
            return false;
        }
        if (!discount.branchNum) {
            alert("Branch number is required.");
            return false;
        }
        if (!/^[0-9]+$/.test(discount.discountVal)) {
            alert("Discount value must be a number.");
            return false;
        }
        if (!/^[0-9]{3}$/.test(discount.branchNum)) {
            alert("Branch number must be exactly 3 digits.");
            return false;
        }
        if (!/^[0-9]{5}$/.test(discount.code)) {
            alert("Code must be exactly 5 digits.");
            return false;
        }
        if (!["time", "amount", "percentage"].includes(discount.discountType)) {
            alert("Invalid discount type.");
            return false;
        }
        return true;
    };

    const handleSave = () => {
        const discountToSave = currentDiscount || newDiscount;

        if (!isDeviationValid) {
            alert("אנא הכנס מספר תקין בשדה הסטייה, הכנסת מספר קטן/גדול מדי");
            return;
        }

        if (!validateFields(discountToSave)) {
            return;
        }

        // ----------------------
        // Update existing discount
        // ----------------------
        if (currentDiscount) {
            const data = new URLSearchParams();
            currentDiscount.placeId = placeId;
            data.append('data', JSON.stringify(currentDiscount));

            fetch(`${config.app.serverUrl}/editdiscount/${currentDiscount.id}?token=${localStorage.getItem('googleAuthToken')}`, {
                method: "POST",
                body: data
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error("Error creating discount");
                    }
                    return response.json();
                })
                .then((updatedDiscountRes) => {
                    setDiscounts((prev) => {
                        const updatedDiscounts = prev.map((discount) => {
                            if (discount.id === updatedDiscountRes.id) {
                                return updatedDiscountRes;
                            }
                            return discount;
                        });
                        return updatedDiscounts;
                    });
                    setOpenDialog(false);
                })
                .catch((error) => {
                    console.error("Error editing discount:", error);
                });


        } else {
            // Attempt to copy over deviation/timeToUpdate/globalNumerator from existing discount
            // if not manually provided
            const foundDiscount = discounts.find(d => d.placeId === placeId);
            if (foundDiscount) {
                if (!newDiscount.deviation) {
                    newDiscount.deviation = foundDiscount.deviation;
                }
                if (!newDiscount.timeToUpdate) {
                    newDiscount.timeToUpdate = foundDiscount.timeToUpdate;
                }
                if (!newDiscount.globalNumerator) {
                    newDiscount.globalNumerator = foundDiscount.globalNumerator;
                }
                if (!newDiscount.lastNumerator) {
                    newDiscount.lastNumerator = foundDiscount.lastNumerator;
                }
            }

            const data = new URLSearchParams();
            newDiscount.placeId = placeId;
            data.append('data', JSON.stringify(newDiscount));

            fetch(`${config.app.serverUrl}/creatediscount?token=${localStorage.getItem('googleAuthToken')}`, {
                method: 'POST',
                body: data,
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error("Error creating discount");
                    }
                    return response.json();
                })
                .then((createdDiscount) => {
                    setDiscounts((prev) => {
                        const updatedDiscounts = [...prev, createdDiscount];
                        // Also update the matching fields for existing discounts if needed
                        const updatedWithFields = updatedDiscounts.map((discount) => {
                            return {
                                ...discount,
                                deviation:
                                    createdDiscount.deviation !== undefined &&
                                    createdDiscount.deviation !== null &&
                                    createdDiscount.deviation !== ''
                                        ? createdDiscount.deviation
                                        : discount.deviation,
                                timeToUpdate:
                                    createdDiscount.timeToUpdate !== undefined &&
                                    createdDiscount.timeToUpdate !== null &&
                                    createdDiscount.timeToUpdate !== ''
                                        ? createdDiscount.timeToUpdate
                                        : discount.timeToUpdate,
                                globalNumerator:
                                    createdDiscount.globalNumerator !== undefined &&
                                    createdDiscount.globalNumerator !== null &&
                                    createdDiscount.globalNumerator !== ''
                                        ? createdDiscount.globalNumerator
                                        : discount.globalNumerator,
                            };
                        });
                        // Adjust page if necessary
                        const newPage = Math.floor(updatedWithFields.length / (rowsPerPage + 1));
                        setPage(newPage);
                        return updatedWithFields;
                    });
                    setOpenDialog(false);
                })
                .catch((error) => {
                    console.error("Error creating discount:", error);
                });
        }
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        setCurrentDiscount(null);
    };

    // ----------------------
    // Pagination
    // ----------------------
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };


    const handleEditLastNumerator = (discount) => {
        // setDiscountForLastNumEdit(discount);
        setNewLastNumerator(discount.lastNumerator?.toString() || '');
        setIsLastNumeratorDialogOpen(true);
    };

    const handleCloseLastNumDialog = () => {
        setIsLastNumeratorDialogOpen(false);
        // setDiscountForLastNumEdit(null);
        setNewLastNumerator('');
    };

    const handleSaveLastNumerator = () => {

        const url = `${config.app.serverUrl}/updatelastnumerator/${placeId}?token=${localStorage.getItem('googleAuthToken')}`;

        const data = new URLSearchParams();
        data.append('data', JSON.stringify({
            lastNumerator: newLastNumerator
        }));

        fetch(url, {
            method: 'POST',
            body: data
        })
            .then((res) => {
                if (!res.ok) {
                    throw new Error('Failed to update lastNumerator');
                }
                return res.json();
            })
            .then((updatedDiscount) => {
                // Update local state
                setDiscounts((prevDiscounts) =>
                    prevDiscounts.map((d) =>
                        d.id === updatedDiscount.id
                            ? { ...d, lastNumerator: newLastNumerator }
                            : { ...d, lastNumerator: newLastNumerator }
                    )
                );
                handleCloseLastNumDialog();
            })
            .catch((error) => {
                console.error('Error updating lastNumerator:', error);
            });
    };


    if (isLoading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <div>
            <Button
                variant="contained"
                color="primary"
                onClick={handleCreate}
                style={{ marginBottom: 16 }}
            >
                Add Discount
            </Button>

            <TableContainer style={{ padding: "20px" }} component={Paper}>
                <Table size="small" aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left" style={{ fontWeight: 'bold' }}>Delete</TableCell>
                            <TableCell align="left" style={{ fontWeight: 'bold' }}>Daily Numerator</TableCell>
                            <TableCell align="left" style={{ fontWeight: 'bold' }}>Current max Numerator</TableCell>
                            <TableCell align="left" style={{ fontWeight: 'bold' }}>Deviation</TableCell>
                            <TableCell align="left" style={{ fontWeight: 'bold' }}>Time To Update Numerator</TableCell>
                            <TableCell align="left" style={{ fontWeight: 'bold' }}>Discount Value</TableCell>
                            <TableCell align="left" style={{ fontWeight: 'bold' }}>Discount Type</TableCell>
                            <TableCell align="left" style={{ fontWeight: 'bold' }}>Code</TableCell>
                            <TableCell align="left" style={{ fontWeight: 'bold' }}>Branch Number</TableCell>
                            {/* NEW column for the "Edit Last Numerator" button */}
                            <TableCell align="left" style={{ fontWeight: 'bold' }}>
                                Edit Max Numerator
                            </TableCell>
                            <TableCell align="left" style={{ fontWeight: 'bold' }}>Edit</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {discounts
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((discount) => (
                                <TableRow key={discount.id}>
                                    <TableCell align="left">
                                        <Button color="error" onClick={() => handleDeleteClick(discount)}>
                                            Delete
                                        </Button>
                                    </TableCell>
                                    <TableCell align="left">{discount.globalNumerator}</TableCell>
                                    <TableCell align="left">{discount.lastNumerator}</TableCell>
                                    <TableCell align="left">{discount.deviation}</TableCell>
                                    <TableCell align="left">{discount.timeToUpdate}</TableCell>
                                    <TableCell align="left">{discount.discountVal}</TableCell>
                                    <TableCell align="left">{discount.discountType}</TableCell>
                                    <TableCell align="left">{discount.code}</TableCell>
                                    <TableCell align="left">{discount.branchNum}</TableCell>
                                    {/* NEW button to specifically edit lastNumerator */}
                                    <TableCell align="left">
                                        <Button color="secondary" onClick={() => handleEditLastNumerator(discount)}>
                                            Edit Max Numerator
                                        </Button>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Button color="primary" onClick={() => handleEdit(discount)}>
                                            Edit
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>

                <TablePagination
                    rowsPerPageOptions={[8]}
                    component="div"
                    count={discounts.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                />
            </TableContainer>

            {/* Existing Dialog for Create/Edit ALL fields except lastNumerator */}
            <Dialog
                fullWidth={isMobile}
                PaperProps={
                    isMobile
                        ? {
                            style: {
                                margin: 0,
                                position: "fixed",
                                top: "25%",
                                left: "90%",
                                transform: "translate(-50%, -50%)",
                                width: "90%",
                            },
                        }
                        : {}
                }
                open={openDialog}
                onClose={handleDialogClose}
            >
                <DialogTitle>{currentDiscount ? 'Edit Discount' : 'Create Discount'}</DialogTitle>
                <DialogContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                    }}
                >
                    <TextField
                        margin="dense"
                        label="Discount Value"
                        name="discountVal"
                        value={currentDiscount ? currentDiscount.discountVal : newDiscount.discountVal}
                        onChange={(e) => handleInputChange(e, !currentDiscount)}
                        fullWidth
                    />

                    {!isMobile ? (
                        <Select
                            margin="dense"
                            label="Discount Type"
                            name="discountType"
                            value={currentDiscount ? currentDiscount.discountType : newDiscount.discountType}
                            onChange={(e) => handleInputChange(e, !currentDiscount)}
                            fullWidth
                        >
                            <MenuItem value="" disabled>Select discount type</MenuItem>
                            <MenuItem value="time">Time</MenuItem>
                            <MenuItem value="amount">Amount</MenuItem>
                            <MenuItem value="percentage">Percentage</MenuItem>
                        </Select>
                    ) : (
                        <select
                            value={currentDiscount ? currentDiscount.discountType : newDiscount.discountType}
                            onChange={(e) => handleInputChange(e, !currentDiscount)}
                            style={{
                                textAlign: 'left',
                                width: '100%',
                                padding: '8px',
                                marginTop: '10px'
                            }}
                        >
                            <option value="" disabled>
                                Select discount type
                            </option>
                            <option value="time">Time</option>
                            <option value="amount">Amount</option>
                            <option value="percentage">Percentage</option>
                        </select>
                    )}

                    <TextField
                        margin="dense"
                        label="Code"
                        name="code"
                        value={currentDiscount ? currentDiscount.code : newDiscount.code}
                        onChange={(e) => handleInputChange(e, !currentDiscount)}
                        fullWidth
                    />

                    <TextField
                        margin="dense"
                        label="Branch Number"
                        name="branchNum"
                        value={currentDiscount ? currentDiscount.branchNum : newDiscount.branchNum}
                        onChange={(e) => handleInputChange(e, !currentDiscount)}
                        fullWidth
                    />

                    <TextField
                        margin="dense"
                        label="Enter deviation"
                        name="deviation"
                        value={currentDiscount ? currentDiscount.deviation : newDiscount.deviation}
                        onChange={(e) => handleInputChange(e, !currentDiscount)}
                        fullWidth
                    />

                    <div style={{ marginTop: 10 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                            <TimePicker
                                value={
                                    currentDiscount
                                        ? currentDiscount.timeToUpdate
                                            ? new Date(`1970-01-01T${currentDiscount.timeToUpdate}:00`)
                                            : null
                                        : newDiscount.timeToUpdate
                                            ? new Date(`1970-01-01T${newDiscount.timeToUpdate}:00`)
                                            : null
                                }
                                onChange={(newValue) => {
                                    if (newValue) {
                                        const formattedTime = `${newValue
                                            .getHours()
                                            .toString()
                                            .padStart(2, '0')}:${newValue
                                            .getMinutes()
                                            .toString()
                                            .padStart(2, '0')}`;
                                        if (currentDiscount) {
                                            setCurrentDiscount((prev) => ({
                                                ...prev,
                                                timeToUpdate: formattedTime
                                            }));
                                        } else {
                                            setNewDiscount((prev) => ({
                                                ...prev,
                                                timeToUpdate: formattedTime
                                            }));
                                        }
                                    }
                                }}
                                renderInput={(params) => (
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <TextField
                                            {...params}
                                            fullWidth
                                            label="Time to Update"
                                            margin="dense"
                                            style={{ flex: 1 }}
                                        />
                                        <span style={{ marginLeft: "10px" }}>Time to Update</span>
                                    </div>
                                )}
                            />
                        </LocalizationProvider>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>Cancel</Button>
                    <Button onClick={handleSave} color="primary">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog for confirm delete */}
            <Dialog
                fullWidth={isMobile}
                PaperProps={
                    isMobile
                        ? {
                            style: {
                                margin: 0,
                                position: "fixed",
                                top: "25%",
                                left: "90%",
                                transform: "translate(-50%, -50%)",
                                width: "90%",
                            },
                        }
                        : {}
                }
                open={doubleConfirmOpen}
                onClose={cancelDelete}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this discount?
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDelete}>Cancel</Button>
                    <Button color="error" onClick={confirmDelete}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                fullWidth={isMobile}
                PaperProps={
                    isMobile
                        ? {
                            style: {
                                margin: 0,
                                position: "fixed",
                                top: "25%",
                                left: "90%",
                                transform: "translate(-50%, -50%)",
                                width: "90%",
                            },
                        }
                        : {}
                }
                open={isLastNumeratorDialogOpen}
                onClose={handleCloseLastNumDialog}
            >
                <DialogTitle>Edit Current Max Numerator</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        label="Last Numerator"
                        value={newLastNumerator}
                        onChange={(e) => {
                            // Only allow numeric input
                            if (/^\d*$/.test(e.target.value)) {
                                setNewLastNumerator(e.target.value);
                            }
                        }}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseLastNumDialog}>Cancel</Button>
                    <Button onClick={handleSaveLastNumerator} color="primary">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default DiscountsTable;
